<script setup lang="ts">
import {
  ChevronDown,
  ChevronUp,
} from "@lucide/vue";
import {
  type Locale, useLocaleStore,
} from "~/stores/locale";
import {
  HEADER_MENU_POPPER_OPTIONS,
} from "~/utils/header-menu";

const props = defineProps<{
  modelValue?: Locale;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: Locale): void;
}>();

const localeStore = useLocaleStore();
const isDropdownOpen = ref(false);

type LanguageElement = {
  code: Locale;
  label: string;
  icon: string;
};

const languages: LanguageElement[] = [
  {code: "de", label: "Deutsch", icon: "🇩🇪"},
  {code: "en", label: "English", icon: "🇺🇸"},
  {code: "vn", label: "Tiếng Việt", icon: "🇻🇳"},
];

const currentLocale = computed(() => {
  return props.modelValue !== undefined ? props.modelValue : localeStore.currentLocale;
});

const currentLanguage = computed(() => {
  return languages.find((lang) => lang.code === currentLocale.value) ?? languages[0] as LanguageElement;
});

const handleLanguageChange = (langCode: string) => {
  const code = langCode as Locale;
  if (props.modelValue !== undefined) {
    emit("update:modelValue", code);
  } else {
    localeStore.setLocale(code);
  }
};

const handleVisibleChange = (visible: boolean) => {
  isDropdownOpen.value = visible;
};
</script>

<template>
  <el-dropdown
    trigger="click"
    :teleported="false"
    :popper-options="HEADER_MENU_POPPER_OPTIONS"
    @command="handleLanguageChange"
    @visible-change="handleVisibleChange"
  >
    <div class="flex items-center justify-center gap-1 cursor-pointer p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
      <span
        class="leading-none text-lg"
      >
        {{ currentLanguage.icon }}
      </span>
      <component
        :is="isDropdownOpen ? ChevronUp : ChevronDown"
        :size="16"
        class="text-gray-600 dark:text-gray-400"
      />
    </div>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="lang in languages"
          :key="lang.code"
          :command="lang.code"
          :disabled="currentLocale === lang.code"
        >
          <span class="mr-2">{{ lang.icon }}</span>
          {{ lang.label }}
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<style scoped>
.el-dropdown-link {
  cursor: pointer;
  display: flex;
  align-items: center;
}
</style>
